/**
 * ErrorBoundary — captura excepciones JS en el árbol de componentes hijo.
 *
 * Uso:
 *   <ErrorBoundary>          ← global (en App.jsx)
 *   <ErrorBoundary view="Dashboard">   ← por vista (fallback inline)
 */
import { Component } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, info: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    this.setState({ info })
    // Reportar a Sentry si está disponible
    if (typeof window !== 'undefined' && window.__Sentry__) {
      window.__Sentry__.captureException(error, { extra: info })
    }
    console.error('[ErrorBoundary]', error, info)

    // Detección de crashes causados por traductores del navegador (Google
    // Translate, Edge, Yandex). El síntoma es siempre el mismo:
    // "Failed to execute 'removeChild' on 'Node': The node to be removed
    // is not a child of this node." Ocurre porque el traductor reorganiza
    // el DOM al vuelo y React choca cuando intenta reemplazar sus propios
    // nodos. Recuperamos silenciosamente reseteando el error state a los
    // 1.5s — para el usuario es como si nunca hubiera pasado. También
    // dejamos el <html translate="no"> en el index para prevenir a nivel
    // hoja, pero si algún operador fuerza traducir con el botón esta red
    // de seguridad evita perder la sesión.
    const msg = String(error?.message || '')
    const isTranslateCrash =
      msg.includes('removeChild') ||
      msg.includes('insertBefore') ||
      msg.includes('not a child of this node') ||
      msg.includes('Failed to execute')

    if (isTranslateCrash && !this._autoRecoverTimer) {
      this._autoRecoverTimer = setTimeout(() => {
        this._autoRecoverTimer = null
        this.setState({ error: null, info: null })
      }, 1500)
    }
  }

  componentWillUnmount() {
    if (this._autoRecoverTimer) clearTimeout(this._autoRecoverTimer)
  }

  render() {
    const { error } = this.state
    const { view, inline } = this.props

    if (!error) return this.props.children

    // Fallback inline (para vistas individuales)
    if (inline) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 10, padding: 32,
          color: 'var(--text-muted)', textAlign: 'center',
        }}>
          <AlertTriangle size={24} style={{ color: 'var(--danger)' }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            Error en {view || 'esta sección'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 300 }}>
            {error.message || 'Error inesperado'}
          </div>
          <button
            onClick={() => this.setState({ error: null, info: null })}
            style={{
              marginTop: 8, display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 'var(--radius-sm)',
              background: 'var(--accent)', color: '#fff',
              border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}
          >
            <RefreshCw size={12} /> Reintentar
          </button>
        </div>
      )
    }

    // Fallback global (pantalla completa)
    return (
      <div style={{
        position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 16,
        background: 'var(--bg-base)', padding: 32, textAlign: 'center',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'var(--danger-light)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <AlertTriangle size={28} style={{ color: 'var(--danger)' }} />
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
            Algo salió mal
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 400 }}>
            {error.message || 'Error inesperado en la aplicación'}
          </div>
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 24px', borderRadius: 'var(--radius-md)',
            background: 'var(--accent)', color: '#fff',
            border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
          }}
        >
          <RefreshCw size={14} /> Recargar aplicación
        </button>
        {import.meta.env.DEV && (
          <pre style={{
            fontSize: 10, color: 'var(--text-muted)', maxWidth: 600,
            textAlign: 'left', overflow: 'auto', maxHeight: 200,
            background: 'var(--bg-surface)', padding: 12, borderRadius: 6,
          }}>
            {error.stack}
          </pre>
        )}
      </div>
    )
  }
}
